import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { timeoutHelpers } from '@core';
import { NotificationsService } from '@ui';
import { firstValueFrom as toPromise } from 'rxjs';

import { WifiConnection } from '../../models';
import { BtService, MqttUsersService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIcon,
    MatInputModule,
    MatProgressSpinner,
    NgTemplateOutlet,
  ],
  selector: 'avs-wifi-picker',
  templateUrl: './wifi-picker.component.html',
})
export class WifiPickerComponent implements OnInit {
  deviceId = input.required<string>();
  isConnected = computed(() => this.btService.isConnected());
  isValid = computed(() => {
    if (this.selectedNetwork() === this.OTHER) {
      return this.customNetwork() && this.password();
    }

    return this.selectedNetwork() && this.password();
  });

  protected btService = inject(BtService);
  protected customNetwork = signal('');
  protected error = signal('');
  protected isLoading = signal(true);
  protected password = signal('');
  protected readonly OTHER = 'other';
  protected selectedNetwork = signal('');
  protected serialNumber = signal('');
  protected wifiNetworks = signal<WifiConnection[]>([]);

  private notificationService = inject(NotificationsService);
  private mqttService = inject(MqttUsersService);

  constructor() {
    effect(() => {
      if (this.selectedNetwork()) {
        this.customNetwork.set('');
        this.password.set('');
      }
    });
  }

  async connect() {
    const password = this.password();
    const ssid = this.selectedNetwork() === this.OTHER
      ? this.customNetwork()
      : this.selectedNetwork();

    try {
      if (!(await this.btService.isBrokerConfigured())) {
        const user = await toPromise(this.mqttService.create(this.deviceId()));

        await this.btService.setMqttCredentials(user.username, user.password);
      }

      await this.btService.setWifiCredentials(ssid, password);
      this.notificationService.showSuccessMessage('WiFi settings saved');

      return true;
    } catch (error) {
      this.error.set((error as Error).message);

      return false;
    }
  }

  async ngOnInit() {
    await timeoutHelpers.delay(500);
    await this.pair();
  }

  protected async pair() {
    this.error.set('');
    this.isLoading.set(true);

    try {
      await this.btService.pair();
      this.wifiNetworks.set(await this.btService.getWifiNetworks());
    } catch (error) {
      this.error.set('Failed to connect to device via Bluetooth. Please ensure Bluetooth is enabled and try again');
    }

    this.isLoading.set(false);
  }
}
